import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import './styles/index.css'
import Dialog from '../common/dialog'
import { FormError } from '../../types/common.types'
import { requestAccessToken } from '../../api/auth.api'
import { AuthContext } from '../../contexts/auth.context'
import { ReactNode, useState, useRef, useContext } from 'react'
import { UpdateBankBalanceForm } from './update-bank-balance-form'
import { updateBankAssignedBalance } from '../../api/payments.api'
import { NotificationsContext } from '../../contexts/notifications.context'

export const UpdateBankBalanceModal = ({
  selectedBankName,
  handleDialogOpenState,
  handleCloseOptionAction
}: {
  selectedBankName: string
  handleCloseOptionAction: () => void
  handleDialogOpenState: (openState: boolean) => void
}) => {
  const navigate = useNavigate()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { t } = useTranslation(['banks-balances-management', 'common'])
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const [errors, setErrors] = useState<FormError>({})
  const updateBalanceOfBankFormRef = useRef<ReactNode | null>(null)
  const [actionButtonDisabled, setActionButtonDisabled] = useState<boolean>(true)
  const [updatingClientBalance, setUpdatingClientBalance] = useState<boolean>(false)

  const handleActionButtonDisableState = (disableState: boolean) => {
    setActionButtonDisabled(disableState)
  }

  const handleUpdateBankAvailableBalance = async () => {
    setErrors({})
    const balanceDataToUpdateToClient = (updateBalanceOfBankFormRef.current as any).balanceDataToDisplay
    setUpdatingClientBalance(true)

    try {
      const extractedNumberFromBalanceDisplayed =
        balanceDataToUpdateToClient[0] === '$' ? Number(balanceDataToUpdateToClient.slice(1).trim().replaceAll(',', '')) : Number(balanceDataToUpdateToClient.trim().replaceAll(',', ''))
      const bankBalanceToUpdate = {
        belongsTo: 'holdings',
        balanceToSet: extractedNumberFromBalanceDisplayed,
        bankBalanceToUpdate: String(selectedBankName) === 'AVVillas' ? 'banco-avvillas' : 'banco-bbva'
      }

      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await updateBankAssignedBalance(bankBalanceToUpdate, accessToken)

      handleCloseOptionAction()
      setUpdatingClientBalance(false)
      updateNewNotificationStatus?.(true, 'client-balance-updated-successfully')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.bankingPartnerNotFound', { ns: 'common' }) })
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setUpdatingClientBalance(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <Dialog
      errors={errors}
      dialogIsLoading={updatingClientBalance}
      actionButtonDisabled={actionButtonDisabled}
      handleDialogOpenState={handleDialogOpenState}
      dialogTitle={t('updateBankBalanceModal.title')}
      handleCloseOptionAction={handleCloseOptionAction}
      handleDialogAction={handleUpdateBankAvailableBalance}
      actionButtonText={t('updateBankBalanceModal.actionButtonText')}
    >
      <UpdateBankBalanceForm
        ref={updateBalanceOfBankFormRef}
        selectedBankName={selectedBankName}
        handleActionButtonDisableState={handleActionButtonDisableState}
      />
    </Dialog>
  )
}
